import { useCallback } from 'react'

import { PLANE_SIDE_LABELS } from '@editor/config/constants'
import { EditorPanelItem, ElementsPanelConfig } from '@editor/interfaces/editor'
import { InspectionItemType } from '@editor/interfaces/inspection'
import { PlaneSide } from '@editor/interfaces/shape'
import { getVolumeEstimationItems } from '@editor/services/VolumeEstimation'
import { RootState, useAppDispatch } from '@editor/store/app'
import { setHoveredElementId } from '@editor/store/tool'
import { useSelector } from 'react-redux'

import {
  PANEL_WORKING_PLANE_LOWER_ID,
  PANEL_WORKING_PLANE_UPPER_ID,
  resetBottomPlane,
  resetTopPlane,
  resetWorkingPoints,
} from '../store/volumeEstimation'

const useElementsPanel = (): ElementsPanelConfig => {
  // Store
  const dispatch = useAppDispatch()
  const inspectionItems = useSelector((state: RootState) => state.inspectionItems.items)
  const shapes = useSelector((state: RootState) => state.shapes.shapes)
  const planes = useSelector((state: RootState) => state.toolVolumeEstimationPolygon.planes)
  const planeCreationOrder = useSelector((state: RootState) => state.toolVolumeEstimationPolygon.planeCreationOrder)
  const polygonWorkingPoints = useSelector((root: RootState) => root.toolVolumeEstimationPolygon.workingPoints)
  const isDirty = useSelector((state: RootState) => state.toolVolumeEstimationPolygon.isDirty)
  const isLoading = useSelector((state: RootState) => state.toolVolumeEstimationPolygon.isSaving)
  const permissionSet = useSelector((state: RootState) => state.editor.permissionSet)
  const userType = useSelector((state: RootState) => state.user.userType)

  // Permission check
  const isAllowedToModify = permissionSet.MODIFY.includes(userType)

  return {
    getPanelItems: useCallback(
      (parent?: EditorPanelItem['item']): EditorPanelItem[] => {
        // Volume is parent-level item, ignore if a parent is provided
        if (parent) return []

        const volumes = getVolumeEstimationItems(inspectionItems)

        if (volumes.length === 0 && !isDirty) return []

        return [
          {
            key: 'panel-volume',
            name: '体積',
            isVirtualContainer: true,
            isCountable: true,
            children: [
              // Saved volumes
              ...volumes.map<EditorPanelItem>((item, index) => ({
                key: `panel-volume-${item.inspection_item_id!}`,
                name: item.part_name || `体積 ${index + 1}`,
                item,
                inspectionItem: item,
                order: item.order_in_inspection_group,
                isGrouped: !!item.inspection_group_id,
                isOrderable: true,
                isCountable: false,
                isVirtualContainer: true,
                onDelete: isAllowedToModify
                  ? [
                      // include polygon shapes
                      ...shapes.polygons
                        .filter((shape) => item.shape_ids.polygons.includes(shape.shape_id))
                        .map((shape) => shape.shape_id),
                      // include grids
                      ...inspectionItems
                        .filter(
                          (it) => it.item_type === InspectionItemType.Grid && it.volume_id === item.inspection_item_id,
                        )
                        .map((it) => it.inspection_item_id!),
                    ]
                  : false,
                children: [
                  {
                    key: `panel-volume-${item.inspection_item_id!}-planes`,
                    name: '平面',
                    itemType: InspectionItemType.Volume,
                    isCountable: false,
                    toggleVisibility: true,
                    isVirtualContainer: true,
                    defaultCollapsed: true,
                    onHover: (hovered) => {
                      dispatch(setHoveredElementId(hovered ? item.inspection_item_id! : ''))
                    },
                    children: [
                      {
                        key: `panel-volume-${item.inspection_item_id}-upper`,
                        name: PLANE_SIDE_LABELS.upper,
                        item: shapes.polygons.find(
                          (shape) =>
                            item.shape_ids.polygons.includes(shape.shape_id) && shape.plane_side === PlaneSide.UPPER,
                        ),
                        isCountable: false,
                        onDelete: false,
                      },
                      {
                        key: `panel-volume-${item.inspection_item_id}-lower`,
                        name: PLANE_SIDE_LABELS.lower,
                        item: shapes.polygons.find(
                          (shape) =>
                            item.shape_ids.polygons.includes(shape.shape_id) && shape.plane_side === PlaneSide.LOWER,
                        ),
                        isCountable: false,
                        onDelete: false,
                      },
                    ],
                  },
                ],
              })),

              // Working planes
              ...(isDirty
                ? [
                    {
                      key: 'panel-volume-working',
                      name: `体積 ${volumes.length + 1}`,
                      itemType: InspectionItemType.Volume,
                      isCountable: false,
                      isSaving: isLoading,
                      isWorking: true,
                      toggleVisibility: !planes[planeCreationOrder[0]] && polygonWorkingPoints.length ? null : true,
                      children: [
                        {
                          key: 'panel-volume-working-planes',
                          name: '平面',
                          isCountable: false,
                          toggleVisibility: !planes[planeCreationOrder[0]] && polygonWorkingPoints.length ? null : true,
                          children: [
                            ...(!planes[planeCreationOrder[0]] && polygonWorkingPoints.length
                              ? [
                                  {
                                    key: 'panel-volume-working-plane-drawing',
                                    name: PLANE_SIDE_LABELS[planeCreationOrder[0]],
                                    toggleVisibility: null,
                                    isSaving: isLoading,
                                    onDelete: isLoading ? false : () => dispatch(resetWorkingPoints()),
                                  },
                                ]
                              : []),
                            ...(planes.upper
                              ? [
                                  {
                                    key: PANEL_WORKING_PLANE_UPPER_ID,
                                    name: PLANE_SIDE_LABELS.upper,
                                    toggleVisibility: true,
                                    onDelete:
                                      (!!planes.lower && planeCreationOrder[0] === PlaneSide.UPPER) || isLoading
                                        ? false
                                        : () => dispatch(resetTopPlane()),
                                  },
                                ]
                              : []),
                            ...(planes.lower
                              ? [
                                  {
                                    key: PANEL_WORKING_PLANE_LOWER_ID,
                                    name: PLANE_SIDE_LABELS.lower,
                                    toggleVisibility: true,
                                    onDelete:
                                      (!!planes.upper && planeCreationOrder[0] === PlaneSide.LOWER) || isLoading
                                        ? false
                                        : () => dispatch(resetBottomPlane()),
                                  },
                                ]
                              : []),
                          ],
                        },
                      ],
                    },
                  ]
                : []),
            ],
          },
        ]
      },
      [
        inspectionItems,
        shapes,
        planeCreationOrder,
        planes,
        isDirty,
        polygonWorkingPoints.length,
        isLoading,
        isAllowedToModify,
        dispatch,
      ],
    ),
  }
}

export default useElementsPanel
